// External libs
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';

// Local libs
import Model from './model';
import { environment } from '../../environments/environment';

/**
 * @fileOverview Generic HTTP service for CRUD operations and file management
 * @description Provides a reusable generic service base class for all API operations.
 * Handles HTTP requests (GET, POST, PUT, DELETE), authentication token management,
 * caching mechanisms, and file upload/download operations with AWS S3 and local server support.
 * 
 * @module service
 */

/**
 * @service Service
 * @template M - Generic type extending Model base class for type-safe API operations
 * @description Generic service base class providing CRUD operations (Create, Read, Update, Delete)
 * and file management functionality. Designed to be extended by domain-specific services
 * (UserService, DeviceService, etc.) for type-safe API interactions.
 * 
 * Key Responsibilities:
 * - Handle HTTP communication with backend API (GET, POST, PUT, DELETE)
 * - Manage authentication tokens in request headers
 * - Implement client-side caching for list operations
 * - Support file uploads to AWS S3 and local servers
 * - Support file downloads from various sources
 * - Generate and manage Excel report downloads
 * 
 * Architecture:
 * - Generic type parameter <M> ensures compile-time type safety
 * - Automatic header construction with authentication tokens
 * - Promise-based API for async operations
 * - Built-in error handling and rejection mechanisms
 * 
 * Features:
 * - Transparent token injection in all requests
 * - Smart caching of list data to reduce server calls
 * - Support for multiple base URLs (API, images, Python backend)
 * - Flexible file operations with configurable paths
 * - Mime-type aware file handling
 * 
 * @example
 * // Extend Service for custom entity
 * interface User extends Model {
 *   email: string;
 *   name: string;
 * }
 * 
 * @Injectable()
 * export class UserService extends Service<User> {
 *   constructor(httpClient: HttpClient) {
 *     super(httpClient, '/users');
 *   }
 * }
 */
export default class Service<M extends Model> {

  /**
   * @property {HttpClient} httpClient
   * @description Angular HttpClient instance for making HTTP requests.
   * Provided via dependency injection and used for all API communication.
   * Marked as readonly to prevent accidental reassignment.
   */
  public readonly httpClient: HttpClient;

  /**
   * @property {string} baseUrl
   * @description Primary API base URL from environment configuration.
   * Used for all standard CRUD operations and file uploads/downloads.
   * Contains the root URL of the backend API server.
   */
  public readonly baseUrl: string;

  /**
   * @property {string} sessionBaseUrl
   * @description Session/authentication API base URL from environment.
   * Reserved for session-specific operations (login, logout, token refresh).
   * Currently defined but not actively used in this base service.
   */
  public readonly sessionBaseUrl: string;

  /**
   * @property {string} imagebaseUrl
   * @description Image service base URL from environment configuration.
   * Dedicated URL endpoint for image-related operations and retrieval.
   * Used when serving image files requires a different server/CDN.
   */
  public readonly imagebaseUrl: string;

  /**
   * @property {string} newPythonUrl
   * @description Python backend service base URL from environment.
   * Used for operations delegated to Python microservices or ML services.
   * Supports specialized data processing and analytics operations.
   */
  public readonly newPythonUrl: string;

  /**
   * @property {string} serviceUrl
   * @description API endpoint path for the specific service/resource.
   * Appended to baseUrl to form complete API URLs.
   * Example: '/users', '/devices', '/subscriptions'
   */
  public readonly serviceUrl: string;

  /**
   * @property {Array<M>} records
   * @description Client-side cache storing the last fetched list of records.
   * Populated by getAll() method to reduce unnecessary server calls.
   * Cleared by clearCache() when data is modified (create, update, delete).
   */
  public records: Array<M>;

  /**
   * @method constructor
   * @description Initializes the service with HTTP client and API endpoint configuration.
   * Sets up all base URLs from environment configuration for multi-environment support
   * (development, staging, production).
   * 
   * @param {HttpClient} httpClient - Angular HttpClient for HTTP operations
   * @param {string} serviceUrl - API endpoint path for this service (e.g., '/users', '/devices')
   */
  constructor(httpClient: HttpClient, serviceUrl: string) {
    this.httpClient = httpClient;
    this.baseUrl = environment.baseUrl;
    this.imagebaseUrl = environment.imagebaseUrl;
    this.newPythonUrl = environment.newPythonBaseUrl
    this.serviceUrl = serviceUrl;
  }

  /**
   * @method httpOptions
   * @description Getter that constructs HTTP headers with authentication tokens for secure API requests.
   * Automatically retrieves current access and refresh tokens from localStorage and includes them
   * in every request. Ensures requests are properly authenticated.
   * 
   * Headers included:
   * - Content-Type: 'application/json' - Request body format
   * - Accept: '/*' - Accept any response format
   * - token: JWT access token from localStorage - Authentication
   * - refreshtoken: Refresh token from localStorage - Token refresh support
   * - pushnotificationkey: Push notification identifier (currently empty string)
   * 
   * @returns {{headers: HttpHeaders}} Object containing HttpHeaders configured with auth tokens
   * 
   * @example
   * // Used internally in all requests
   * this.httpClient.get(url, this.httpOptions);
   */
  public get httpOptions() {
    const token = localStorage.getItem('token');
    const refreshtoken = localStorage.getItem('refreshtoken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': '*/*',
      'token': token || '',
      'refreshtoken': refreshtoken || '',
      'pushnotificationkey': ''
    });
    return {
      headers: headers
    };
  }

  /**
   * @method create
   * @description Creates a new resource on the server using HTTP POST request.
   * Clears cached records to ensure fresh data is fetched on next getAll() call.
   * Constructs full URL as baseUrl + serviceUrl.
   * 
   * @async
   * @param {M} model - The data model object to create on the server
   * @returns {Promise<M>} Promise resolving to the created entity from server response
   * @throws {Error} Rejects with error message from server if creation fails
   * 
   * @example
   * const newUser = { id: '', email: 'new@example.com', name: 'John', createdAt: new Date(), updatedAt: new Date() };
   * const created = await userService.create(newUser);
   */
  public create(model: M) {
    return new Promise<M>((resolve, reject) => {
      this.clearCache();
      this.httpClient.post<M>(`${this.baseUrl}${this.serviceUrl}`, model, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error) => {
        reject(error.error.message);
      });
    });
  }

  /**
   * @method getAll
   * @description Fetches all resources of type M from the server using HTTP GET request.
   * Implements smart caching: if records are already cached, returns cached data immediately
   * without making a server request. Cache is cleared when data is modified (create, update, delete).
   * 
   * Cache Benefits:
   * - Reduces server load for frequently accessed data
   * - Improves UI responsiveness with instant data retrieval
   * - Better user experience for list operations
   * 
   * @async
   * @returns {Promise<Array<M>>} Promise resolving to array of all resources
   * Returns cached records if available, otherwise fetches from server
   * @throws {HttpErrorResponse} Rejects with HTTP error if server request fails
   * 
   * @example
   * const allUsers = await userService.getAll();
   * // Second call returns cached data immediately without server request
   * const cachedUsers = await userService.getAll();
   */
  public getAll() {
    return new Promise<Array<M>>((resolve, reject) => {
      if (this.records) {
        resolve(this.records);
      } else {
        this.httpClient.get<Array<M>>(`${this.baseUrl}${this.serviceUrl}`, this.httpOptions).subscribe(response => {
          this.records = response;
          resolve(this.records);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
      }
    });
  }

  /**
   * @method getOneByID
   * @description Fetches a single resource by its unique identifier using HTTP GET request.
   * Constructs URL as baseUrl + serviceUrl + 'id/{id}'
   * Does not use caching - always fetches fresh data from server for single resource detail view.
   * 
   * @async
   * @param {string} id - The unique identifier of the resource to fetch
   * @returns {Promise<M>} Promise resolving to the single resource entity
   * @throws {HttpErrorResponse} Rejects with HTTP error if resource not found or server error
   * 
   * @example
   * const user = await userService.getOneByID('user-123');
   */
  public getOneByID(id: string) {
    return new Promise<M>((resolve, reject) => {
      this.httpClient.get<M>(`${this.baseUrl}${this.serviceUrl}id/${id}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
    });
  }

  /**
   * @method updateOneByID
   * @description Updates an existing resource on the server using HTTP PUT request.
   * Clears cached records to ensure fresh data is fetched on next getAll() call.
   * Constructs URL as baseUrl + serviceUrl + 'id/{id}'
   * 
   * @async
   * @param {string} id - The unique identifier of the resource to update
   * @param {M} model - The updated model object with new values
   * @returns {Promise<M>} Promise resolving to the updated entity from server response
   * @throws {Error} Rejects with error message from server if update fails
   * 
   * @example
   * const updated = await userService.updateOneByID('user-123', { ...user, name: 'Jane' });
   */
  public updateOneByID(id: string, model: M) {
    return new Promise<M>((resolve, reject) => {
      this.clearCache();
      this.httpClient.put<M>(`${this.baseUrl}${this.serviceUrl}id/${id}`, model, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error) => {
        reject(error.error.message);
      });
    });
  }

  /**
   * @method deleteOneByID
   * @description Deletes a resource from the server using HTTP DELETE request.
   * Constructs URL as baseUrl + serviceUrl + 'id/{id}'
   * Note: Does not explicitly clear cache - caller should handle cache invalidation if needed.
   * 
   * @async
   * @param {string} id - The unique identifier of the resource to delete
   * @returns {Promise<M>} Promise resolving to the response from server (may be confirmation object)
   * @throws {Error} Rejects with error message from server if deletion fails
   * 
   * @example
   * const result = await userService.deleteOneByID('user-123');
   */
  public deleteOneByID(id: string) {
    return new Promise<M>((resolve, reject) => {
      this.httpClient.delete<M>(`${this.baseUrl}${this.serviceUrl}id/${id}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error) => {
        reject(error.error.message);
      });
    });
  }

  /**
   * @method uploadFileToS3Bucket
   * @description Uploads a file to AWS S3 bucket using HTTP PUT request.
   * Constructs dynamic S3 path with business name, functionality path, and file name.
   * Removes default Content-Type header and sets it to the file's actual mime type.
   * 
   * S3 Path Structure:
   * Customer_Assets/Blubay_Web/{businessName}/{functionalityPath}/{fileName}
   * 
   * @param {File} file - The file object to upload
   * @param {any} businessName - Business identifier for path organization (spaces converted to underscores)
   * @param {string} functionalityPath - Feature/module path (e.g., 'profile', 'documents')
   * @param {string} fileName - Name to save file as in S3
   * @returns {Observable} RxJS Observable for the HTTP PUT request (can be converted to Promise)
   * 
   * @example
   * const file = fileInput.files[0];
   * const observable = this.service.uploadFileToS3Bucket(file, 'MyBusiness', 'profile', 'avatar.jpg');
   * observable.subscribe(response => console.log('Uploaded to S3'));
   */
  public uploadFileToS3Bucket(file: File, businessName: any, functionalityPath: string, fileName: string) {
    let headers = this.httpOptions.headers;
    headers = headers.delete('Content-Type');
    headers = headers.set('Content-Type', file.type);
    return this.httpClient.put(`${this.baseUrl}/${this.serviceUrl.split('/')[1]}/s3upload?filePath=Customer_Assets/Blubay_Web/${businessName && businessName.replace(/ /g,"_")}/${functionalityPath}/${fileName}`, file, { headers: headers});
  }

  /**
   * @method downloadFileFromS3Bucket
   * @description Downloads a file from AWS S3 bucket using HTTP GET request.
   * Returns response as blob for direct file download in browser.
   * 
   * @param {any} filePath - Full path to the file in S3 bucket
   * @returns {Observable<Blob>} RxJS Observable emitting file blob for download
   * 
   * @example
   * const filePath = 'Customer_Assets/Blubay_Web/MyBusiness/profile/avatar.jpg';
   * const observable = this.service.downloadFileFromS3Bucket(filePath);
   * observable.subscribe(blob => {
   *   const url = window.URL.createObjectURL(blob);
   *   const link = document.createElement('a');
   *   link.href = url;
   *   link.download = 'avatar.jpg';
   *   link.click();
   * });
   */
  public downloadFileFromS3Bucket(filePath: any) {
    let headers = this.httpOptions.headers;
    return this.httpClient.get(`${this.baseUrl}/${this.serviceUrl.split('/')[1]}/s3download?filePath=${filePath}`, { headers: headers, responseType: 'blob' });
  }

  /**
   * @method uploadFileToServer
   * @description Uploads a file to the local server using HTTP PUT request.
   * Extracts file extension from file name for backend processing.
   * Removes default Content-Type header and sets it to the file's actual mime type.
   * 
   * Server Path Structure:
   * Customer_Assets/Blubay_Web/{businessName}/{functionalityPath}/?fileName={fileName}&fileType={extension}
   * 
   * @param {File} file - The file object to upload
   * @param {any} businessName - Business identifier for path organization (spaces converted to underscores)
   * @param {string} functionalityPath - Feature/module path (e.g., 'profile', 'documents')
   * @param {string} fileName - Name to save file as (extension will be preserved)
   * @returns {Observable} RxJS Observable for the HTTP PUT request
   * 
   * @example
   * const file = fileInput.files[0];
   * const observable = this.service.uploadFileToServer(file, 'MyBusiness', 'profile', 'avatar.jpg');
   * observable.subscribe(response => console.log('Uploaded to server'));
   */
  public uploadFileToServer(file: File, businessName: any, functionalityPath: string, fileName: string) {
    let paths = file.name.split('.');
    let headers = this.httpOptions.headers;
    headers = headers.delete('Content-Type');
    headers = headers.set('Content-Type', file.type);
    return this.httpClient.put(`${this.baseUrl}/${this.serviceUrl.split('/')[1]}/uploadFile?directory=Customer_Assets/Blubay_Web/${businessName && businessName.replace(/ /g,"_")}/${functionalityPath}/&fileName=${fileName.split('.')[0]}&fileType=${paths[paths.length-1]}`, file, { headers: headers});
  }

  /**
   * @method downloadFileFromServer
   * @description Downloads a file from the local server using HTTP GET request.
   * Returns response as blob for direct file download in browser.
   * Sets Content-Type header based on requested file type.
   * 
   * @param {string} fileType - MIME type of the file (e.g., 'application/pdf', 'image/jpeg')
   * @param {string} filePath - Full path to the file on server
   * @returns {Observable<Blob>} RxJS Observable emitting file blob for download
   * 
   * @example
   * const observable = this.service.downloadFileFromServer('application/pdf', 'Customer_Assets/Blubay_Web/MyBusiness/documents/report.pdf');
   * observable.subscribe(blob => {
   *   const url = window.URL.createObjectURL(blob);
   *   const link = document.createElement('a');
   *   link.href = url;
   *   link.download = 'report.pdf';
   *   link.click();
   * });
   */
  public downloadFileFromServer(fileType: string, filePath: string) {
    let headers = this.httpOptions.headers;
    headers = headers.delete('Content-Type');
    headers = headers.set('Content-Type', fileType);
    return this.httpClient.get(`${this.baseUrl}/${this.serviceUrl.split('/')[1]}/downloadFile?filePath=${filePath}`, { headers: headers, responseType: 'blob'});
  }

  /**
   * @method excelReportsDownload
   * @description Generates and downloads an Excel report from the backend using HTTP POST request.
   * Sends input data/filters to backend which generates the Excel file on-the-fly.
   * Returns response as blob for direct download in browser.
   * 
   * @param {string} fileName - Name for the generated Excel file (without extension)
   * @param {any} input - Report parameters/filters to pass to backend
   * (structure depends on backend report generation implementation)
   * @returns {Observable<Blob>} RxJS Observable emitting Excel file blob for download
   * 
   * @example
   * const reportInput = { startDate: '2026-01-01', endDate: '2026-01-31', deviceId: 'device-123' };
   * const observable = this.service.excelReportsDownload('device_report_january', reportInput);
   * observable.subscribe(blob => {
   *   const url = window.URL.createObjectURL(blob);
   *   const link = document.createElement('a');
   *   link.href = url;
   *   link.download = 'device_report_january.xlsx';
   *   link.click();
   * });
   */
  public excelReportsDownload(fileName: string, input: any) {
    let headers = this.httpOptions.headers;
    return this.httpClient.post(`${this.baseUrl}/${this.serviceUrl.split('/')[1]}/generateExcel/download?name=${fileName}`, input, { headers: headers, responseType: 'blob' });
  }

  /**
   * @method clearCache
   * @description Clears the client-side cache of fetched records.
   * Should be called after create, update, or delete operations to ensure
   * fresh data is fetched on the next getAll() call.
   * 
   * @returns {void}
   * 
   * @example
   * await this.service.create(newUser);
   * this.service.clearCache(); // Clear old list data
   * const freshList = await this.service.getAll(); // Fetch fresh data from server
   */
  public clearCache() {
    this.records  = undefined;
  }
}
